import { useConfigContext } from '@fe-core/context';
import {
  FaIcon,
  ListItem,
  Resource,
  useMarkResourceAsRead,
} from '@fe-learner-common';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons/faArrowUpRightFromSquare';
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload';
import { forwardRef } from 'react';
import { buildResourceAriaLabel } from './buildResourceAriaLabel';
import { ResourceActions } from './ResourceActions';
import { ListItemProps } from './ResourceListItem';

export const Link = forwardRef<
  HTMLAnchorElement,
  Omit<ListItemProps, 'onResourceClick' | 'container'>
>(({ resource, participationId, texts, onDescriptionClick, ...rest }, ref) => {
  const { config } = useConfigContext();
  const { mutate: markAsRead } = useMarkResourceAsRead({
    resourceId: resource.rid,
    participationId,
  });
  const url =
    resource.link ||
    `${config.apiUrls.aleweb}/participation/${participationId}/resource/${resource.rid}/useDirect`;

  return (
    <ListItem>
      <a
        ref={ref}
        className="absolute inset-0 z-0 rounded-lg"
        onClick={() => {
          markAsRead();
        }}
        href={url}
        target={resource.useMode === 'newwindow' ? '_blank' : '_self'}
        rel="noreferrer"
        aria-label={buildResourceAriaLabel({
          type: texts.groupTypes[resource.groupType],
          title: resource.material.webTitle,
          tags: resource.tags,
        })}
        {...rest}
      />
      <div className="flex flex-1 items-start justify-between gap-x-4 p-3">
        <Resource
          resourceId={resource.rid}
          title={resource.material.webTitle}
          groupType={resource.groupType}
          thumbnail={resource.material.thumbnail}
          tags={resource.tags}
          isNew={resource.newResource}
          texts={texts}
        />
        <ResourceActions
          resourceId={resource.rid}
          hasDescription={!!resource.material.webDescription}
          onDescriptionClick={onDescriptionClick}
          descriptionAriaLabel={texts.descriptionAriaLabel}
        >
          <a
            href={url}
            target={resource.useMode === 'newwindow' ? '_blank' : '_self'}
            rel="noreferrer"
            className="l-btn l-btn-sm l-btn-tertiary l-icon-btn hover:bg-neutral-50"
            onClick={() => {
              markAsRead();
            }}
            aria-label={
              resource.material.productType === 'genericfile'
                ? texts.downloadResourceAriaLabel
                : texts.openExternalResourceAriaLabel
            }
          >
            <FaIcon
              width={16}
              height={16}
              icon={
                resource.material.productType === 'genericfile'
                  ? faDownload
                  : faArrowUpRightFromSquare
              }
            />
          </a>
        </ResourceActions>
      </div>
    </ListItem>
  );
});
Link.displayName = 'LinkListItem';
